import { format, isSameDay, subDays } from "date-fns";
import { useEffect, useState } from "react";
import { Empty, EmptyDescription, EmptyHeader } from "@repo/ui/components/ui/empty";
import { ItemGroup } from "@repo/ui/components/ui/item";
import { AGENT_NAME } from "./agentName";
import { HistoryEntryRow } from "./HistoryEntryRow";
import { historyTabStyles as s } from "./HistoryTab.styles";
import type { HistoryEntry, HistoryTabProps } from "./HistoryTab.types";
export type { HistoryEntry } from "./HistoryTab.types";

const GROUPS = ["Today", "Yesterday", "Earlier"] as const;
type HistoryGroup = (typeof GROUPS)[number];
type ResolvedHistoryEntry = { entry: HistoryEntry; date?: Date };

function parseTimestamp(value?: string): Date | undefined {
  if (!value) return undefined;

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date;
}

function dateGroup(date: Date | undefined, now: Date): HistoryGroup {
  if (!date) return "Earlier";
  if (isSameDay(date, now)) return "Today";
  if (isSameDay(date, subDays(now, 1))) return "Yesterday";
  return "Earlier";
}

function resolveTimestamp(entry: HistoryEntry, now: Date): Date | undefined {
  const date = parseTimestamp(entry.createdAt) ?? parseTimestamp(entry.updatedAt);
  if (!date) return undefined;
  return date.getTime() > now.getTime() ? now : date;
}

function formatTimestamp(date: Date | undefined, group: HistoryGroup, now: Date): string {
  if (!date) return "";
  if (group === "Earlier") return format(date, "MMM d");

  const elapsedMinutes = Math.max(0, Math.floor((now.getTime() - date.getTime()) / 60_000));
  if (group === "Today" && elapsedMinutes < 60) return `${elapsedMinutes}m ago`;
  return `${Math.floor(elapsedMinutes / 60)}h ago`;
}

function useCurrentMinute(): Date {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;

    function scheduleNextMinute() {
      const currentTime = new Date();
      const millisecondsUntilNextMinute = 60_000 - (currentTime.getTime() % 60_000);

      timeoutId = setTimeout(() => {
        setNow(new Date());
        scheduleNextMinute();
      }, millisecondsUntilNextMinute);
    }

    scheduleNextMinute();
    return () => clearTimeout(timeoutId);
  }, []);

  return now;
}

export function HistoryTab({
  entries,
  onSelect,
  onRename,
  onDelete,
  loading,
  error,
}: HistoryTabProps) {
  const now = useCurrentMinute();

  if (loading) {
    return (
      <Empty className="flex-1 px-6">
        <EmptyHeader>
          <EmptyDescription className={s.emptyDesc}>Loading conversations…</EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  if (error) {
    return (
      <Empty className="flex-1 px-6">
        <EmptyHeader>
          <EmptyDescription className={s.emptyDesc}>{error}</EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  if (entries.length === 0) {
    return (
      <Empty className="flex-1 px-6">
        <EmptyHeader>
          <EmptyDescription className={s.emptyDesc}>
            Your recent {AGENT_NAME} activity will appear here.
          </EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  const groupedEntries = new Map<HistoryGroup, ResolvedHistoryEntry[]>();

  for (const entry of entries) {
    const date = resolveTimestamp(entry, now);
    const group = dateGroup(date, now);
    const groupEntries = groupedEntries.get(group) ?? [];
    groupEntries.push({ entry, date });
    groupedEntries.set(group, groupEntries);
  }

  return (
    <div className={s.root}>
      {GROUPS.map((group) => {
        const groupEntries = groupedEntries.get(group);
        if (!groupEntries) return null;

        return (
          <div key={group}>
            <div className={s.groupHeader}>
              <p className={s.groupLabel}>{group}</p>
            </div>
            <ItemGroup>
              {groupEntries.map(({ entry, date }) => (
                <HistoryEntryRow
                  key={entry.id}
                  entry={entry}
                  timestamp={formatTimestamp(date, group, now)}
                  onSelect={() => onSelect?.(entry)}
                  onRename={onRename ? (title) => onRename(entry, title) : undefined}
                  onDelete={onDelete ? () => onDelete(entry) : undefined}
                />
              ))}
            </ItemGroup>
          </div>
        );
      })}
    </div>
  );
}
